import React, { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PromptDetailDrawer from "@/components/PromptDetailDrawer";
import { useProject } from "@/context/ProjectContext";
import api from "@/lib/api";
import { Plus, Trash2, Library, ListOrdered, Check, ChevronRight } from "lucide-react";

const CATEGORIES = [
  { value: "category", label: "Category" },
  { value: "comparison", label: "Comparison" },
  { value: "problem_solution", label: "Problem → Solution" },
  { value: "recommendation", label: "Recommendation" },
  { value: "custom", label: "Custom" },
];

const CAT_CLASS = {
  category: "bg-zinc-100 text-zinc-700 border-zinc-200",
  comparison: "bg-blue-50 text-blue-700 border-blue-200",
  problem_solution: "bg-amber-50 text-amber-700 border-amber-200",
  recommendation: "bg-emerald-50 text-emerald-700 border-emerald-200",
  custom: "bg-zinc-50 text-zinc-600 border-zinc-200",
};

export default function Prompts() {
  const { current, projects, selectProject } = useProject();
  const [tab, setTab] = useState("yours");
  const [prompts, setPrompts] = useState([]);
  const [text, setText] = useState("");
  const [category, setCategory] = useState("category");
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activePromptId, setActivePromptId] = useState(null);

  const refresh = async () => {
    if (!current) return;
    const r = await api.get(`/projects/${current.project_id}/prompts`);
    setPrompts(r.data);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.project_id]);

  const addOne = async (e) => {
    e.preventDefault();
    if (!text.trim() || !current) return;
    setLoading(true);
    try {
      await api.post(`/projects/${current.project_id}/prompts`, {
        text: text.trim(),
        category,
      });
      setText("");
      await refresh();
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/prompts/${id}`);
    refresh();
  };

  const openDrawer = (id) => {
    setActivePromptId(id);
    setDrawerOpen(true);
  };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setActivePromptId(null), 250);
  };

  return (
    <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
      <div className="mb-8">
        <div className="label-eyebrow">Monitoring</div>
        <h1 className="display mt-2 text-4xl font-black tracking-tighter">
          Master Prompt List
        </h1>
        <p className="mt-2 text-zinc-600 text-sm max-w-xl">
          Add the 20–30 queries your customers actually ask. Click any prompt
          to see how each engine answers it. Browse the Library tab to import
          starter prompts for your industry.
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-zinc-200 flex gap-1" data-testid="prompts-tabs">
        <TabButton
          active={tab === "yours"}
          onClick={() => setTab("yours")}
          icon={ListOrdered}
          testid="tab-yours"
        >
          Your prompts ({prompts.length})
        </TabButton>
        <TabButton
          active={tab === "library"}
          onClick={() => setTab("library")}
          icon={Library}
          testid="tab-library"
        >
          Library
        </TabButton>
      </div>

      {tab === "yours" && (
        <YoursTab
          prompts={prompts}
          text={text}
          setText={setText}
          category={category}
          setCategory={setCategory}
          loading={loading}
          onAdd={addOne}
          onRemove={remove}
          onOpenDetail={openDrawer}
        />
      )}

      {tab === "library" && current && (
        <LibraryTab projectId={current.project_id} onImported={refresh} existing={prompts} />
      )}

      <PromptDetailDrawer
        open={drawerOpen}
        promptId={activePromptId}
        onClose={closeDrawer}
      />
    </DashboardShell>
  );
}

function TabButton({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 -mb-[1px] transition-colors ${
        active
          ? "border-black text-zinc-900 font-medium"
          : "border-transparent text-zinc-500 hover:text-zinc-900"
      }`}
    >
      <Icon size={14} /> {children}
    </button>
  );
}

function YoursTab({ prompts, text, setText, category, setCategory, loading, onAdd, onRemove, onOpenDetail }) {
  return (
    <>
      <section className="card-flat p-5 mb-6" data-testid="add-prompt-card">
        <form onSubmit={onAdd} className="grid grid-cols-12 gap-3 items-start">
          <div className="col-span-12 lg:col-span-8">
            <label className="label-eyebrow">Prompt</label>
            <input
              className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
              placeholder="e.g. Best project management tool for startups"
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="prompt-input"
            />
          </div>
          <div className="col-span-8 lg:col-span-3">
            <label className="label-eyebrow">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="prompt-category"
              className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-4 lg:col-span-1 flex items-end">
            <button
              type="submit"
              disabled={!text.trim() || loading}
              data-testid="prompt-add"
              className="w-full inline-flex justify-center items-center gap-1.5 bg-black text-white text-sm px-3 py-2 rounded-[4px] hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              <Plus size={14} />
            </button>
          </div>
        </form>
        <div className="mt-4 flex items-center justify-between border-t border-zinc-200 pt-4 text-xs text-zinc-500">
          <span>
            {prompts.length} prompt{prompts.length === 1 ? "" : "s"} · aim for 20–30
          </span>
          <span className="mono uppercase tracking-[0.2em]">
            Use the Library tab to import starter packs
          </span>
        </div>
      </section>

      <section className="card-flat" data-testid="prompts-list">
        <div className="divide-y divide-zinc-200">
          {prompts.map((p) => (
            <div
              key={p.prompt_id}
              className="p-4 grid grid-cols-12 gap-4 items-center text-sm cursor-pointer hover:bg-zinc-50 transition-colors group"
              onClick={() => onOpenDetail?.(p.prompt_id)}
              data-testid={`prompt-row-${p.prompt_id}`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onOpenDetail?.(p.prompt_id);
                }
              }}
            >
              <div className="col-span-2">
                <span
                  className={`mono text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full border ${
                    CAT_CLASS[p.category] || CAT_CLASS.custom
                  }`}
                >
                  {p.category.replace("_", " ")}
                </span>
              </div>
              <div className="col-span-9 text-zinc-800 flex items-center gap-2">
                {p.text}
                <ChevronRight
                  size={14}
                  className="text-zinc-300 group-hover:text-zinc-500 group-hover:translate-x-0.5 transition-all"
                />
              </div>
              <div className="col-span-1 text-right">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(p.prompt_id);
                  }}
                  data-testid={`prompt-delete-${p.prompt_id}`}
                  className="text-zinc-400 hover:text-rose-600 transition-colors"
                  aria-label="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
          {!prompts.length && (
            <div className="p-8 text-sm text-zinc-500 text-center">
              No prompts yet. Add one above or jump to the Library tab.
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function LibraryTab({ projectId, onImported, existing }) {
  const [industries, setIndustries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/prompt-library").then((r) => {
      setIndustries(r.data);
      if (r.data.length) setSelected(r.data[0].industry_id);
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/prompt-library/${selected}`).then((r) => setDetail(r.data));
  }, [selected]);

  const existingTexts = useMemo(() => new Set(existing.map((p) => p.text)), [existing]);

  const visiblePrompts = useMemo(() => {
    if (!detail) return [];
    if (filter === "all") return detail.prompts;
    return detail.prompts.filter((p) => p.category === filter);
  }, [detail, filter]);

  const importAll = async () => {
    if (!detail) return;
    setImporting(true);
    try {
      const r = await api.post(`/projects/${projectId}/prompts/from-library`, {
        industry_id: detail.industry_id,
      });
      setImported({ added: r.data.added, skipped: r.data.skipped });
      onImported();
      setTimeout(() => setImported(null), 3000);
    } finally {
      setImporting(false);
    }
  };

  const importOne = async (idx) => {
    if (!detail) return;
    await api.post(`/projects/${projectId}/prompts/from-library`, {
      industry_id: detail.industry_id,
      indexes: [idx],
    });
    onImported();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid="library-tab">
      <aside className="lg:col-span-3 card-flat p-3" data-testid="library-industries">
        <div className="px-2 py-2 label-eyebrow">Industries</div>
        <ul>
          {industries.map((i) => (
            <li key={i.industry_id}>
              <button
                onClick={() => setSelected(i.industry_id)}
                data-testid={`industry-${i.industry_id}`}
                className={`w-full text-left px-2 py-2 rounded-[4px] text-sm transition-colors ${
                  selected === i.industry_id
                    ? "bg-zinc-900 text-white"
                    : "text-zinc-700 hover:bg-zinc-100"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{i.label}</span>
                  <span
                    className={`mono text-[10px] ${
                      selected === i.industry_id ? "text-zinc-300" : "text-zinc-500"
                    }`}
                  >
                    {i.total}
                  </span>
                </div>
                <div
                  className={`text-[11px] mt-0.5 ${
                    selected === i.industry_id ? "text-zinc-300" : "text-zinc-500"
                  }`}
                >
                  {i.description}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="lg:col-span-9 card-flat" data-testid="library-detail">
        {detail ? (
          <>
            <div className="p-5 border-b border-zinc-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="label-eyebrow">Starter pack</div>
                <h2 className="display mt-1 text-2xl font-bold tracking-tight">
                  {detail.label}
                </h2>
                <p className="text-xs text-zinc-500 mt-1">
                  {detail.description}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  data-testid="library-filter"
                  className="bg-white border border-zinc-200 rounded-[4px] px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-black/10"
                >
                  <option value="all">All categories</option>
                  {CATEGORIES.filter((c) => c.value !== "custom").map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={importAll}
                  disabled={importing}
                  data-testid="library-import-all"
                  className="inline-flex items-center gap-1.5 bg-black text-white text-sm px-3.5 py-2 rounded-[4px] hover:bg-zinc-800 transition-colors disabled:opacity-50"
                >
                  {importing ? "Importing…" : `Add all ${detail.prompts.length}`}
                </button>
              </div>
            </div>

            {imported && (
              <div
                className="px-5 py-2 bg-emerald-50 text-emerald-700 text-xs border-b border-emerald-200 flex items-center gap-2"
                data-testid="library-imported"
              >
                <Check size={12} /> Imported {imported.added} · Skipped {imported.skipped} (already in project)
              </div>
            )}

            <div className="divide-y divide-zinc-200">
              {visiblePrompts.map((p, idx) => {
                // find real index in source list for the API
                const realIdx = detail.prompts.findIndex(
                  (x) => x.text === p.text && x.category === p.category
                );
                const already = existingTexts.has(p.text);
                return (
                  <div
                    key={idx}
                    className="p-4 grid grid-cols-12 gap-3 items-center text-sm"
                    data-testid={`lib-row-${realIdx}`}
                  >
                    <div className="col-span-2">
                      <span
                        className={`mono text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full border ${
                          CAT_CLASS[p.category] || CAT_CLASS.custom
                        }`}
                      >
                        {p.category.replace("_", " ")}
                      </span>
                    </div>
                    <div className="col-span-8 text-zinc-800">{p.text}</div>
                    <div className="col-span-2 text-right">
                      {already ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500">
                          <Check size={12} /> Added
                        </span>
                      ) : (
                        <button
                          onClick={() => importOne(realIdx)}
                          data-testid={`lib-add-${realIdx}`}
                          className="inline-flex items-center gap-1 text-xs border border-zinc-900 px-2.5 py-1 rounded-[4px] hover:bg-zinc-100 transition-colors"
                        >
                          <Plus size={12} /> Add
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {!visiblePrompts.length && (
                <div className="p-8 text-center text-sm text-zinc-500">
                  No prompts in this filter.
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="p-8 text-center text-sm text-zinc-500">
            Loading library…
          </div>
        )}
      </section>
    </div>
  );
}
